import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Dropdown } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';

import { UseOutletContextPayroll } from './Payroll';
import PayrollAccessDenied from './PayrollAccessDenied';
import { StateLanguage } from '../../../languages/config/StateLanguage';
import { PayrollRun, PayrollRunStatus, PayrollRunPagination } from '../../../models/payroll-run.model';
import { PayrollRunDA } from '../../../services/admin/payroll-run.service';
import { formatMoney } from '../../../scripts/format.value.script';
import { dateFormat } from '../../../scripts/date.time.format.script';
import { usePaginationUrl } from '../../../hooks/usePaginationUrl';
import AppPagination from '../../../components/element/Pagination';
import AppPreloader from '../../../components/Preloader';

const MONTH_KEYS = [
  'january', 'february', 'march', 'april', 'may', 'june',
  'july', 'august', 'september', 'october', 'november', 'december',
] as const;

const RUN_STATUSES: PayrollRunStatus[] = ['draft', 'calculating', 'calculated', 'validated', 'paid', 'cancelled'];

function monthName(lang: any, month: number): string {
  return lang.labels[MONTH_KEYS[month - 1]] ?? String(month);
}

function statusBadgeClass(status: PayrollRunStatus): string {
  switch (status) {
    case 'draft': return 'badge-light-warning';
    case 'calculating': return 'badge-light-info';
    case 'calculated': return 'badge-light-primary';
    case 'validated': return 'badge-light-success';
    case 'paid': return 'badge-light-success';
    case 'cancelled': return 'badge-light-secondary';
    default: return 'badge-light-primary';
  }
}

function statusLabel(lang: any, status: PayrollRunStatus): string {
  const key = `runStatus${status.charAt(0).toUpperCase() + status.slice(1)}` as keyof typeof lang.labels;
  return lang.labels[key] ?? status;
}

const createRunSchema = z.object({
  year: z.coerce.number().min(2020).max(2100),
  month: z.coerce.number().min(1).max(12),
});

const PayrollRunListPage: React.FunctionComponent = () => {
  const { setRoute, companyForUser } = UseOutletContextPayroll();
  const { lang } = StateLanguage();
  const navigate = useNavigate();
  const { page: urlPage, setPage: setUrlPage } = usePaginationUrl();

  const [runs, setRuns] = useState<PayrollRun[] | null>(null);
  const [pagination, setPagination] = useState<PayrollRunPagination>({ page: 1, limit: 20, total: 0, pages: 1 });
  const [filterYear, setFilterYear] = useState<number>(new Date().getFullYear());
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [accessDenied, setAccessDenied] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [existingRunId, setExistingRunId] = useState<string | null>(null);

  const now = new Date();
  const createForm = useForm({
    resolver: zodResolver(createRunSchema),
    defaultValues: {
      year: now.getFullYear(),
      month: now.getMonth() + 1,
    },
  });

  const loadRuns = async (page: number) => {
    if (!companyForUser) return;
    const response = await PayrollRunDA.getPayrollRuns(companyForUser.id, {
      year: filterYear || undefined,
      status: filterStatus || undefined,
      page,
      limit: pagination.limit,
    });

    if (response.status === 200) {
      const data = response.data;
      const items: PayrollRun[] = Array.isArray(data) ? data : (data?.items ?? []);
      const pag = data?.pagination ?? { page, limit: pagination.limit, total: items.length, pages: 1 };
      setRuns(items);
      setPagination(pag);
      setAccessDenied(false);
    } else if (response.status === 403) {
      setAccessDenied(true);
      setRuns([]);
    } else {
      setRuns([]);
      setPagination({ page: 1, limit: pagination.limit, total: 0, pages: 1 });
    }
  };

  const handleFilter = () => {
    setUrlPage(1);
    loadRuns(1);
  };

  const handleResetFilter = () => {
    setFilterYear(new Date().getFullYear());
    setFilterStatus('');
    setUrlPage(1);
    // Load after state update — use current year
    if (companyForUser) {
      PayrollRunDA.getPayrollRuns(companyForUser.id, { year: new Date().getFullYear(), page: 1, limit: pagination.limit }).then(response => {
        if (response.status === 200) {
          const data = response.data;
          setRuns(Array.isArray(data) ? data : (data?.items ?? []));
          setPagination(data?.pagination ?? { page: 1, limit: pagination.limit, total: 0, pages: 1 });
        }
      });
    }
  };

  const loadPagination = (page: number) => {
    setUrlPage(page);
    loadRuns(page);
  };

  const handleCreate = createForm.handleSubmit(
    async (data) => {
      if (!companyForUser) return;
      setCreating(true);
      setCreateError(null);
      setExistingRunId(null);

      const response = await PayrollRunDA.createPayrollRun(companyForUser.id, data);

      if (response.status === 200 || response.status === 201) {
        toast.success(lang.labels.actionSuccessful || 'Success');
        setShowCreateForm(false);
        const newRun = response.data;
        if (newRun?.id) {
          navigate(`/main/payroll/runs/view/${newRun.id}`);
        } else {
          loadRuns(1);
        }
      } else if (response.status === 409) {
        // Period already exists
        setCreateError(lang.labels.periodAlreadyExists);
        setExistingRunId(response.data?.existing_run_id ?? response.data?.id ?? null);
      } else {
        setCreateError(response.data?.message || lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain);
      }

      setCreating(false);
    },
  );

  useEffect(() => {
    if (companyForUser) {
      setRuns(null);
      loadRuns(urlPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser]);

  useEffect(() => {
    setRoute({ path: { root: lang.labels.payroll, branch: lang.labels.payrollRuns }, company: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (accessDenied) {
    return <PayrollAccessDenied />;
  }

  return (
    <div className="w-100 h-100">
      {runs
        ?
        <div className="card card-flush">
          <div className="card-header">
            <div className="card-title">
              <h4 className="fw-bolder">{lang.labels.payrollRuns}</h4>
            </div>
            <div className="card-toolbar">
              <div className="d-flex justify-content-end align-items-center">
                <Dropdown>
                  <Dropdown.Toggle variant={`btn ${filterStatus ? "btn-primary" : "btn-light-primary"} me-2`}>
                    <div className="d-flex align-items-center">
                      <span className="svg-icon svg-icon-2 ms-0">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <path d="M19.0759 3H4.72777C3.95892 3 3.47768 3.83148 3.86067 4.49814L8.56967 12.6949C9.17923 13.7559 9.5 14.9582 9.5 16.1819V19.5072C9.5 20.2189 10.2223 20.7028 10.8805 20.432L13.8805 19.1977C14.2553 19.0435 14.5 18.6783 14.5 18.273V13.8372C14.5 12.8089 14.8171 11.8056 15.408 10.964L19.8943 4.57465C20.3596 3.912 19.8856 3 19.0759 3Z" fill="black" />
                        </svg>
                      </span>
                      {lang.labels.filter}
                    </div>
                  </Dropdown.Toggle>
                  <Dropdown.Menu variant="menu menu-sub menu-sub-dropdown w-300px w-md-325px" align="end">
                    <div className="mt-5 mx-5">
                      <div className="fs-5 text-dark fw-bolder">{lang.labels.filterOptions}</div>
                    </div>
                    <div className="separator border-gray-200"></div>
                    <div className="mt-3 m-5">
                      <div className="mb-5">
                        <label className="form-label fs-7">{lang.labels.year}:</label>
                        <input
                          className="form-control form-control-sm"
                          type="number"
                          value={filterYear}
                          onChange={e => setFilterYear(Number(e.target.value))}
                        />
                      </div>
                      <div className="mb-5">
                        <label className="form-label fs-7">{lang.labels.status}:</label>
                        <select className="form-select form-select-sm" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                          <option value="">{lang.labels.allStatuses}</option>
                          {RUN_STATUSES.map(s => (
                            <option key={s} value={s}>{statusLabel(lang, s)}</option>
                          ))}
                        </select>
                      </div>
                      <div className="d-flex justify-content-end">
                        <Dropdown.Item bsPrefix="btn btn-sm btn-light btn-active-light-primary" onClick={handleResetFilter}>{lang.labels.reset}</Dropdown.Item>
                        <Dropdown.Item bsPrefix="btn btn-sm btn-primary ms-3" onClick={handleFilter}>{lang.labels.apply}</Dropdown.Item>
                      </div>
                    </div>
                  </Dropdown.Menu>
                </Dropdown>
                <button className="btn btn-primary" onClick={() => { setShowCreateForm(true); setCreateError(null); setExistingRunId(null); }}>
                  <div className="d-flex align-items-center">
                    <span className="svg-icon svg-icon-2 ms-0">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <rect opacity="0.3" x="2" y="2" width="20" height="20" rx="5" fill="black" />
                        <rect x="10.8891" y="17.8033" width="12" height="2" rx="1" transform="rotate(-90 10.8891 17.8033)" fill="black" />
                        <rect x="6.01041" y="10.9247" width="12" height="2" rx="1" fill="black" />
                      </svg>
                    </span>
                  </div>
                </button>
              </div>
            </div>
          </div>
          <div className="card-body pt-0">
            {/* Create form */}
            {showCreateForm && (
              <div className="card bg-light mb-5">
                <div className="card-body">
                  <h5 className="fw-bolder mb-5">{lang.labels.createPayrollRun}</h5>
                  <form onSubmit={handleCreate}>
                    <div className="row">
                      <div className="col-md-4">
                        <label className="form-label required">{lang.labels.year}</label>
                        <input
                          className="form-control"
                          type="number"
                          {...createForm.register('year')}
                        />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label required">{lang.labels.period}</label>
                        <select className="form-select" {...createForm.register('month')}>
                          {MONTH_KEYS.map((key, i) => (
                            <option key={key} value={i + 1}>{lang.labels[key]}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    {createError && (
                      <div className="alert alert-danger d-flex align-items-center mt-3">
                        <i className="bi bi-exclamation-circle-fill me-2"></i>
                        <div>
                          <span>{createError}</span>
                          {existingRunId && (
                            <Link to={`/main/payroll/runs/view/${existingRunId}`} className="ms-2 fw-bold">
                              {lang.labels.periodAlreadyExistsLink}
                            </Link>
                          )}
                        </div>
                      </div>
                    )}
                    <div className="d-flex justify-content-end mt-5">
                      <button type="button" className="btn btn-light me-3" onClick={() => setShowCreateForm(false)}>
                        {lang.labels.cancel}
                      </button>
                      <button type="submit" className="btn btn-primary" disabled={creating}>
                        {creating ? lang.labels.pleaseWait : lang.labels.save}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Table */}
            <div className="dataTables_wrapper">
              <div className="table-responsive">
                <table className="table align-middle table-row-dashed mb-2">
                  <thead className="border-bottom">
                    <tr className="text-start text-muted text-uppercase fw-bolder fs-7">
                      <th className="min-w-120px">{lang.labels.period}</th>
                      <th className="min-w-100px">{lang.labels.status}</th>
                      <th className="min-w-80px">{lang.labels.employeeCount}</th>
                      <th className="min-w-100px">{lang.labels.grossTotal}</th>
                      <th className="min-w-100px">{lang.labels.netTotal}</th>
                      <th className="min-w-120px">{lang.labels.employerCost}</th>
                      <th className="min-w-120px">{lang.labels.validatedAt}</th>
                      <th className="w-75px text-end">{lang.labels.actions}</th>
                    </tr>
                  </thead>
                  {runs.length > 0
                    ?
                    <tbody className="text-secondary fw-bold">
                      {runs.map(run => (
                        <tr key={run.id} className="row-dashed">
                          <td>
                            <Link to={`/main/payroll/runs/view/${run.id}`} className="fw-bolder text-dark text-hover-primary">
                              {monthName(lang, run.period.month)} {run.period.year}
                            </Link>
                          </td>
                          <td>
                            <span className={`badge ${statusBadgeClass(run.status)}`}>
                              {statusLabel(lang, run.status)}
                            </span>
                          </td>
                          <td>{run.employee_count ?? 0}</td>
                          <td>{formatMoney(run.totals?.gross)}</td>
                          <td>{formatMoney(run.totals?.net)}</td>
                          <td>{formatMoney(run.totals?.employer_cost)}</td>
                          <td>{run.validated_at ? dateFormat(lang.code, run.validated_at) : '-'}</td>
                          <td className="text-end">
                            <Link to={`/main/payroll/runs/view/${run.id}`} className="btn btn-sm btn-light btn-active-light-primary">
                              {lang.labels.view}
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    :
                    <tbody>
                      <tr className="text-secondary fw-bold">
                        <td colSpan={8} className="text-center">{lang.labels.noPayrollRuns}</td>
                      </tr>
                    </tbody>
                  }
                </table>
              </div>
              <AppPagination
                showOption={true}
                pagination={{
                  current: pagination.page,
                  total: pagination.total,
                  limit: pagination.limit,
                  pages: Array.from({ length: pagination.pages }, (_, i) => i + 1),
                }}
                loadPagination={loadPagination}
              />
            </div>
          </div>
        </div>
        :
        <AppPreloader />
      }
    </div>
  );
};

export default PayrollRunListPage;
